/**
 * Main Application Component
 * STAK MIDI Configurator
 */

import { useConfigStore, selectMappingMode } from '@/store/configStore';
import type { MappingMode } from '@/store/configStore';
import { MainLayout, type TabConfig } from '@/components/layout/MainLayout';
import { CollapsiblePanel } from '@/components/layout/CollapsiblePanel';
import { GridSizePanel } from '@/components/panels/GridSizePanel';
import { NoteMappingPanel } from '@/components/panels/NoteMappingPanel';
import { LEDSettingsPanel } from '@/components/panels/LEDSettingsPanel';
import { DefaultColorsPanel } from '@/components/panels/DefaultColorsPanel';
import { ScaleColoringPanel } from '@/components/panels/ScaleColoringPanel';
import { ZonesPanel } from '@/components/panels/ZonesPanel';
import { OverridesPanel } from '@/components/panels/OverridesPanel';
import { AnalogInputsPanel } from '@/components/panels/AnalogInputsPanel';
import { LEDOverridesPanel } from '@/components/panels/LEDOverridesPanel';
import { LEDLayersPanel } from '@/components/panels/LEDLayersPanel';
import { ExportImportPanel } from '@/components/panels/ExportImportPanel';
import { DevicePanel } from '@/components/panels/DevicePanel';
import { FirmwareUpdatePanel } from '@/components/panels/FirmwareUpdatePanel';
import { TuningPanel } from '@/components/panels/TuningPanel';
import { PresetsPanel } from '@/components/panels/PresetsPanel';
import { SettingsPanel } from '@/components/panels/SettingsPanel';
import { LayerBar } from '@/components/layout/LayerBar';
import { HexKeyboardView } from '@/components/hex/HexKeyboardView';

function MappingModeSelector() {
  const mappingMode = useConfigStore(selectMappingMode);
  const setTuningEnabled = useConfigStore((state) => state.setTuningEnabled);

  const options: { value: MappingMode; label: string }[] = [
    { value: 'note_mapping', label: 'Note Mapping' },
    { value: 'microtonal', label: 'Microtonal Tuning' },
  ];

  return (
    <div style={{
      display: 'flex',
      gap: '0',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {options.map((opt) => {
        const active = mappingMode === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => setTuningEnabled(opt.value === 'microtonal')}
            style={{
              flex: 1,
              padding: '8px 12px',
              fontSize: '13px',
              fontWeight: active ? 600 : 400,
              border: 'none',
              backgroundColor: active ? '#e94560' : '#fff',
              color: active ? '#fff' : '#6c757d',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function App() {
  const columns = useConfigStore((state) => state.columns);
  const rows = useConfigStore((state) => state.rows);
  const globalNoteMapping = useConfigStore((state) => state.noteMapping);
  const layers = useConfigStore((state) => state.layers);
  const ledSettings = useConfigStore((state) => state.ledSettings);
  const ledDefaultBehavior = useConfigStore((state) => state.ledDefaultBehavior);
  const scaleColoring = useConfigStore((state) => state.scaleColoring);
  const zones = useConfigStore((state) => state.zones);
  const overrides = useConfigStore((state) => state.overrides);
  const ledOverrides = useConfigStore((state) => state.ledOverrides);
  const ledLayers = useConfigStore((state) => state.ledLayers);
  const selectedKey = useConfigStore((state) => state.selectedKey);
  const activeLayer = useConfigStore((state) => state.activeLayer);
  const setSelectedKey = useConfigStore((state) => state.setSelectedKey);
  const refreshTrigger = useConfigStore((state) => state.refreshTrigger);

  // Get effective note mapping for active layer (falls back to global)
  const currentLayer = layers.find((l) => l.id === activeLayer);
  const effectiveNoteMapping = currentLayer?.note_mapping ?? globalNoteMapping;

  // Define tabs with their panels
  const tabs: TabConfig[] = [
    {
      id: 'layout',
      label: 'Layout',
      icon: '⌨',
      panels: (
        <>
          <MappingModeSelector />
          <CollapsiblePanel title="Note Mapping" panelId="note-mapping" defaultOpen={true}>
            <NoteMappingPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Microtonal Tuning" panelId="tuning" defaultOpen={false}>
            <TuningPanel />
          </CollapsiblePanel>
        </>
      ),
    },
    {
      id: 'keys',
      label: 'Keys',
      icon: '🎹',
      panels: (
        <>
          <CollapsiblePanel title="Zones" panelId="midi-zones" defaultOpen={true}>
            <ZonesPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="MIDI Overrides" panelId="midi-overrides" defaultOpen={true}>
            <OverridesPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="LED Overrides" panelId="midi-led-overrides" defaultOpen={false}>
            <LEDOverridesPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Analog Inputs" panelId="midi-analog" defaultOpen={false}>
            <AnalogInputsPanel />
          </CollapsiblePanel>
        </>
      ),
    },
    {
      id: 'led',
      label: 'LED',
      icon: '💡',
      panels: (
        <>
          <CollapsiblePanel title="Settings" panelId="led-settings" defaultOpen={true}>
            <LEDSettingsPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Default Colors" panelId="led-default-colors" defaultOpen={true}>
            <DefaultColorsPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Scale Coloring" panelId="led-scale-coloring" defaultOpen={false}>
            <ScaleColoringPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Layer Colors" panelId="led-layers" defaultOpen={false}>
            <LEDLayersPanel />
          </CollapsiblePanel>
        </>
      ),
    },
    {
      id: 'device',
      label: 'Device',
      icon: '📁',
      panels: (
        <>
          <CollapsiblePanel title="Device Connection" panelId="device-connection" defaultOpen={true}>
            <DevicePanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Presets" panelId="device-presets" defaultOpen={false}>
            <PresetsPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Configuration" panelId="device-config" defaultOpen={false}>
            <ExportImportPanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Firmware Update" panelId="device-firmware" defaultOpen={false}>
            <FirmwareUpdatePanel />
          </CollapsiblePanel>
        </>
      ),
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: '⚙',
      panels: (
        <>
          <CollapsiblePanel title="Grid Size" panelId="grid-size" defaultOpen={true}>
            <GridSizePanel />
          </CollapsiblePanel>
          <CollapsiblePanel title="Hardware" panelId="settings-hardware" defaultOpen={false}>
            <SettingsPanel />
          </CollapsiblePanel>
        </>
      ),
    },
  ];

  return (
    <MainLayout
      tabs={tabs}
      defaultTab="layout"
      toolbar={<LayerBar />}
      visualization={
        <HexKeyboardView
          columns={columns}
          rows={rows}
          noteMapping={effectiveNoteMapping}
          ledSettings={ledSettings}
          ledDefaultBehavior={ledDefaultBehavior}
          scaleColoring={scaleColoring}
          zones={zones}
          overrides={overrides}
          ledOverrides={ledOverrides}
          ledLayers={ledLayers}
          selectedKey={selectedKey}
          activeLayer={activeLayer}
          refreshTrigger={refreshTrigger}
          hexSize={30}
          onKeyClick={setSelectedKey}
        />
      }
    />
  );
}

export default App;
